'use client';

import {useState, useEffect} from 'react';
import {useLocale} from 'next-intl';

interface ScrapedPage {
  title: string;
  content_fr: string;
  content_ar: string;
  images: string[];
  meta_description: string;
}

interface CategoryData {
  category: string;
  page: string;
  section: string;
  pages: ScrapedPage[];
}

export default function ServicesActifsPage() {
  const locale = useLocale();
  const [content, setContent] = useState<ScrapedPage | null>(null);
  const [gallery, setGallery] = useState<string[]>([]);

  useEffect(() => {
    fetch('/data/scraped_content.json')
      .then(r => r.json())
      .then((data: Record<string, CategoryData>) => {
        const cat = data['services-actifs'];
        if (cat?.pages?.length > 0) {
          const page = cat.pages[0];
          setContent(page);
          // Filter unique, meaningful images
          const imgs = page.images.filter((img: string) =>
            img && !img.includes('spacer') && !img.includes('icon')
          );
          setGallery([...new Set(imgs)].slice(0, 12));
        }
      })
      .catch(() => {});
  }, []);

  const text = content
    ? (locale === 'ar' ? content.content_ar || content.content_fr : content.content_fr)
    : '';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <h1 className="text-4xl font-bold text-primary mb-6">
        {locale === 'ar' ? 'الخدمات النشطة' : 'Services Actifs'}
      </h1>

      <div className="bg-primary/5 border-s-4 border-primary rounded-lg p-6 mb-12">
        <p className="text-lg text-foreground leading-relaxed">
          {locale === 'ar'
            ? 'المديرية الفرعية لوسائل التدخل مكلفة باستقبال وتخزين وفرز وتوزيع المنتجات المختلفة المخصصة لتنفيذ جميع البرامج الاجتماعية للاتحاد.'
            : 'La Sous Direction des moyens d\'interventions est chargee de la reception, du stockage, du tri et de la distribution des differents produits destines a la realisation de tous les programmes sociaux de l\'UTSS.'}
        </p>
      </div>

      {/* Content from scraped data */}
      {text && (
        <div className="prose prose-lg max-w-none mb-12">
          {text.split('\n').filter(Boolean).map((paragraph, i) => {
            if (paragraph.startsWith('#')) {
              const level = paragraph.match(/^#+/)?.[0].length || 2;
              const headingText = paragraph.replace(/^#+\s*/, '');
              if (level <= 2) return <h2 key={i} className="text-2xl font-bold text-primary mt-8 mb-4">{headingText}</h2>;
              if (level <= 3) return <h3 key={i} className="text-xl font-semibold text-primary mt-6 mb-3">{headingText}</h3>;
              return <h4 key={i} className="text-lg font-semibold text-primary-light mt-4 mb-2">{headingText}</h4>;
            }
            return <p key={i} className="text-muted mb-3 leading-relaxed">{paragraph}</p>;
          })}
        </div>
      )}

      {/* Key stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-white border border-border rounded-xl p-6 text-center shadow-sm">
          <div className="text-3xl font-bold text-primary mb-2">11</div>
          <div className="text-muted text-sm">Dépôts a travers le pays</div>
        </div>
        <div className="bg-white border border-border rounded-xl p-6 text-center shadow-sm">
          <div className="text-3xl font-bold text-primary mb-2">9 500 m&sup2;</div>
          <div className="text-muted text-sm">Superficie totale de stockage</div>
        </div>
        <div className="bg-white border border-border rounded-xl p-6 text-center shadow-sm">
          <div className="text-3xl font-bold text-primary mb-2">~9 000 T</div>
          <div className="text-muted text-sm">Capacite de stockage</div>
        </div>
      </div>

      {/* Image gallery */}
      {gallery.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-primary mb-6">Galerie</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.map((img, i) => (
              <div key={i} className="aspect-[4/3] rounded-lg overflow-hidden bg-surface">
                <img
                  src={img}
                  alt={`Service actif UTSS ${i + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
